import styles from './DiscountEditor.module.scss'
import useTranslation from '../Hooks/useTranslation'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faXmark, faCheck } from '@fortawesome/free-solid-svg-icons'
import { faTrashCan } from '@fortawesome/pro-light-svg-icons'
import Button from './Button'
import usePrefStore from '../stores/prefs'
import { useEffect, useRef, useState } from 'react'
import useHelpers from '../Hooks/useHelpers'

interface DiscountEditorProps {
   value: number | null | undefined
   icon?: React.ReactNode
   title?: string
   onCancel: () => void
   onValidate: (discount: number) => void
   top?: string
   minWidth?: string
   maxWidth?: string
}

function DiscountEditor(props: DiscountEditorProps) {
   const { trans } = useTranslation()
   const { inlineStyleFromProps } = useHelpers()
   // Initialisation synchrone avec props.value pour que la valeur soit présente au 1er render,
   // sinon le select() ci-dessous s'appliquerait à une chaîne vide.
   const [text, setText] = useState<string>(() => String(props.value ?? 0))
   const [addStyle, setAddStyle] = useState<any>({})
   const { globalClosePopups } = usePrefStore()
   const inputRef = useRef<HTMLInputElement | null>(null)

   useEffect(() => {
      const style = inlineStyleFromProps({ top: props.top, minWidth: props.minWidth, maxWidth: props.maxWidth })
      setAddStyle(style)
   }, [props.top, props.minWidth, props.maxWidth, inlineStyleFromProps])

   // Sélectionne tout le contenu de l'input à l'ouverture pour remplacer facilement au clavier.
   // requestAnimationFrame garantit qu'on attend la fin du 1er render et du focus auto.
   useEffect(() => {
      const id = requestAnimationFrame(() => {
         inputRef.current?.focus()
         inputRef.current?.select()
      })
      return () => cancelAnimationFrame(id)
   }, [])

   const parsedValue = Number(text.replace(',', '.'))
   const isValid = text.trim() !== '' && !isNaN(parsedValue) && parsedValue >= 0 && parsedValue <= 100

   function doNothing(evt) {
      evt.stopPropagation()
   }

   function doCancel() {
      if (props.onCancel) props.onCancel()
   }

   function doOK() {
      if (!isValid) return
      props.onValidate(parsedValue)
   }

   function onClickBackground() {
      if (props.onCancel && globalClosePopups !== 'button') props.onCancel()
   }

   function onChange(evt) {
      const v = evt.target.value
      // Autoriser vide, chiffres, un point/virgule et signes de saisie
      if (v === '' || /^\d*[.,]?\d*$/.test(v)) {
         setText(v)
      }
   }

   function doClean(evt) {
      evt.stopPropagation()
      setText('0')
   }

   function onSubmit(evt) {
      evt.preventDefault()
      doOK()
   }

   useEffect(() => {
      function onKey(e: KeyboardEvent) {
         if (e.key === 'Enter') {
            e.preventDefault()
            if (isValid) props.onValidate(parsedValue)
         } else if (e.key === 'Escape') {
            e.preventDefault()
            if (props.onCancel) props.onCancel()
         }
      }
      window.addEventListener('keydown', onKey)
      return () => window.removeEventListener('keydown', onKey)
   }, [isValid, parsedValue, props])

   return (
      <div className={styles.background} onClick={onClickBackground}>
         <div className={styles.discount_editor} onClick={doNothing} style={addStyle}>
            <div className={styles.top}>
               {props.icon}
               <span>{props.title ?? trans('?Remise ligne')}</span>
               <button onClick={doCancel}>
                  <FontAwesomeIcon icon={faXmark} />
               </button>
            </div>
            <div className={styles.detail}>
               <form onSubmit={onSubmit}>
                  <div className={styles.input_row}>
                     <input ref={inputRef} autoFocus type="text" inputMode="decimal" value={text} onChange={onChange} onFocus={(e) => e.target.select()} />
                     <span className={styles.unit}>%</span>
                  </div>
               </form>
               {!isValid && <div className={styles.error}>{trans('?Remise invalide (0-100)')}</div>}
            </div>
            <div className={styles.footer}>
               <Button tertiary lefticon={<FontAwesomeIcon icon={faTrashCan} />} onClick={doClean} />
               <Button tertiary lefticon={<FontAwesomeIcon icon={faXmark} />} onClick={doCancel}>
                  {trans('?Annuler')}
               </Button>
               <Button disabled={!isValid} lefticon={<FontAwesomeIcon icon={faCheck} />} onClick={doOK}>
                  {trans('?Valider')}
               </Button>
            </div>
         </div>
      </div>
   )
}

export default DiscountEditor
